import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel
} from '@mui/material';
import {UserActivityTypes} from "../../types";
import {When} from "react-if";
import NoData from "../no-data";
import {Team} from "../../types/teams.types";
import {convertWeekYearsToDisplay} from "../../helpers/weekYearDisplayFormat";
import Loading from "../loading";

interface Props {
    loading : boolean;
    data: UserActivityTypes.WeeklyActivityData;
    allTeams : Team[];
    onSort?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortOrder?: 'ASC' | 'DESC';
}

interface TeamData {
    teamName: string;
    weekYear: string;
    totalActiveUsers: number;
    totalUsers: number;
    totalInactiveUsers: number;
    activeUserPercentage: string;
}

interface TabularRow {
    teamName: string;
    [weekYear: string]: string | number; // Dynamic columns for each week
}

// Alternative function that returns the column headers separately
const transformToTabularDataWithHeaders = (data: UserActivityTypes.WeeklyActivityData, allTeams : Team[]): {
    headers: string[];
    rows: TabularRow[];
} => {
    // Get all unique weeks and sort them
    const allWeeks = new Set<string>();
    Object.values(data).forEach(teamDataArray => {
        teamDataArray.forEach(monthData => {
            allWeeks.add(monthData.weekYear);
        });
    });

    const allWeeksArray = Array.from(allWeeks).sort()
    const headers = ['teamName', ...allWeeksArray];

    // Transform data into tabular format
    const rows: TabularRow[] = Object.entries(data).map(([teamId, teamDataArray]) => {
        // Find the team name from allTeams using teamId
        const teamName = teamId === 'Unassigned' ? 'No Team Assigned' : (allTeams.find(t => t.id === teamId)?.name ?? '');
        const row: TabularRow = { teamName };

        // Create a map for quick lookup of week data
        const weeklyDataMap = new Map<string, TeamData>();
        teamDataArray.forEach(monthData => {
            weeklyDataMap.set(monthData.weekYear, monthData);
        });

        // Add each week's activeUserPercentage to the row
        allWeeksArray.forEach(week => {
            const weekData = weeklyDataMap.get(week);
            row[week] = weekData ? parseFloat(weekData.activeUserPercentage) : 0;
        });

        return row;
    });

    return { headers, rows };
}


const styles = {
    container: {
        borderRadius: 1,
        border: '0.1px solid #ccc',
        backgroundColor: 'white',
        padding: '3rem 1.5rem 3rem 1.5rem'
    }
};

export const ActiveUserPercentPerTeamTable : React.FC<Props>  = ({
                                                                     data,
                                                                     loading,
                                                                     allTeams,
                                                                     onSort,
                                                                     currentSortBy = 'teamName',
                                                                     currentSortOrder = 'ASC'
                                                                 })=> {

    //remove No Team Assigned key data from data and put it again at last
    let tableData = data;
    if (data && data['Unassigned']) {
        const noTeamData = data['Unassigned'];
        tableData = Object.keys(data).reduce((acc, key) => {
            if (key !== 'Unassigned') {
                acc[key] = data[key];
            }
            return acc;
        }, {} as UserActivityTypes.WeeklyActivityData);
        tableData['Unassigned'] = noTeamData;
    }

    const handleSort = (column: string) => {
        if (!onSort || column !== 'teamName') return;

        let newSortOrder: 'ASC' | 'DESC' = 'ASC';

        if (currentSortBy === column) {
            // Toggle sort order if clicking the same column
            newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            // Set new column and default to ascending
            newSortOrder = 'ASC';
        }

        onSort(column, newSortOrder);
    };

    const columns : string[] = transformToTabularDataWithHeaders(tableData,allTeams).headers;
    const displayColumns : string[] = ['teamName', ...convertWeekYearsToDisplay(columns.slice(1))];
    const rows : TabularRow[] = transformToTabularDataWithHeaders(tableData,allTeams).rows;

    return (
        <div style={{ width: '100%', height: 400, overflow: 'auto' }}>
            <TableContainer sx={{ borderRadius: 0, maxHeight: 400, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                <When condition={
                    !loading && (!data || Object.keys(data).length === 0)
                }>

                    <Box sx={{...styles.container}}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No data available'}
                            description={
                                'There are no user activity records available'
                            }
                        />
                    </Box>

                </When>
                <When condition={ data && Object.keys(data).length > 0}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow sx={{borderBottom: '4px solid white' }} key={'header-row'}>
                                {displayColumns.map((column, i) => (
                                    <TableCell
                                        sx={{
                                            backgroundColor: '#BDBABA !important',
                                            backgroundImage: 'none',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            padding: '8px 16px'
                                        }}
                                        key={i}
                                        align={i > 0 ? 'center' : 'inherit'}
                                    >
                                        {i === 0 && onSort ? (
                                            <TableSortLabel
                                                active={currentSortBy === 'teamName'}
                                                direction={currentSortBy === 'teamName' ? currentSortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                                                onClick={!loading ? () => handleSort('teamName') : undefined}
                                                sx={{
                                                    '& .MuiTableSortLabel-root': {
                                                        color: '#000000',
                                                        fontWeight: 700,
                                                        fontSize: 14
                                                    },
                                                    '& .MuiTableSortLabel-root:hover': {
                                                        color: '#333333'
                                                    },
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: '#000000 !important'
                                                    }
                                                }}
                                            >
                                                <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                    {column === "teamName" ? "Team Name / Work Week" : column}
                                                </Typography>
                                            </TableSortLabel>
                                        ) : (
                                            <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                {column === "teamName" ? "Team Name / Work Week" : column}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading}>
                            <TableBody>
                                {rows.map((row,index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            borderBottom: '4px solid white',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#F5F4F4'
                                            },
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#DDF5FF'
                                            },
                                            ":hover": {
                                                backgroundColor: '#dbd8d8',
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        {columns.map((col, i) => (
                                            <TableCell key={i} align={i > 0 ? 'center' : 'inherit'}>{col === 'teamName' ? row[col] : `${Math.round(Number(row[col]))}%`}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                </When>
                <When condition={loading}>
                    <Loading />
                </When>
            </TableContainer>
        </div>
    );
}